import {useQuery} from "@tanstack/react-query";
import {useContext, useEffect, useState} from "react";

import type {MessageRule} from "../../../models/messageRule";
import {updateListWS} from "../../../utils";
import WebSocketContext from "../../contexts/WebSocketContext";
import {useMessageRuleService} from "../useMessageRuleService";

type Params = {
    listingID?: string;
    listingGroupID?: string;
    isDemo?: boolean;
};

export const useWatchMessageRule = ({listingID, listingGroupID, isDemo}: Params) => {
    const ws = useContext(WebSocketContext);
    const messageRuleService = useMessageRuleService();
    const [messageRules, setMessageRules] = useState<MessageRule[] | undefined>();

    const {data, refetch, isLoading} = useQuery({
        queryKey: ["/getMessageRules", listingID, listingGroupID],
        queryFn: async () => {
            if (listingGroupID) {
                return messageRuleService.get(
                    "/getMessageRules/:listingID/:listingGroupID?",
                    {
                        listingID: "group",
                        listingGroupID
                    },
                    {
                        demo: isDemo ? "true" : undefined
                    }
                );
            }
            if (listingID) {
                return messageRuleService.get(
                    "/getMessageRules/:listingID/:listingGroupID?",
                    {
                        listingID
                    },
                    {
                        demo: isDemo ? "true" : undefined
                    }
                );
            }
            return messageRuleService.get("/getMessageRules", undefined, {
                demo: isDemo ? "true" : undefined
            });
        }
    });

    useEffect(() => {
        setMessageRules(data);
    }, [data]);

    useEffect(() => {
        function syncMessageRule(payload: WSPayload<MessageRule>) {
            if (
                payload.event === "update" &&
                (payload.document.listingID === listingID ||
                    payload.document.listingGroupID === listingGroupID)
            ) {
                // TODO: this might lead to a bug if we don't update the cache data
                // but the request will fire again when the component is first mounted
                setMessageRules(prev => {
                    if (!prev) {
                        return prev;
                    }
                    const rules = updateListWS(prev, payload);
                    return rules;
                });
            } else if (
                payload.event === "insert" &&
                (payload.document.listingID === listingID ||
                    payload.document.listingGroupID === listingGroupID)
            ) {
                refetch();
            } else if (payload.event === "delete") {
                refetch();
            }
        }

        ws?.on<WSRoom>("messageRule", syncMessageRule);
        return () => {
            ws?.off("messageRule", syncMessageRule);
        };
    }, [ws, refetch, listingID, listingGroupID]);

    return {
        isLoading,
        messageRules,
        refetch
    };
};
